/**
 * MicrobiomeTwinPage — iter609
 * Diet-inferred Gut Diversity Score + symptom tracker + Claude insights.
 * Без външни лаб тестове — анализира meal_log + BG ферментирали + пребиотици.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Leaf, Activity } from "lucide-react";
import GiAbsorptionCard from "@/components/microbiome/GiAbsorptionCard";
import RefluxBrief from "@/components/microbiome/RefluxBrief";
import { toast } from "sonner";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function MicrobiomeTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);
  const [bristol, setBristol] = useState(null);
  const [bloating, setBloating] = useState(0);
  const [gas, setGas] = useState(0);
  const [logging, setLogging] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/microbiome-twin/insights`, {}, { timeout: 35000 });
      setSnapshot(data.snapshot);
      setInsight(data.insight_bg || "");
    } catch (e) {
      // fallback to snapshot only
      try {
        const { data: snap } = await axios.get(`${API}/microbiome-twin/snapshot`);
        setSnapshot(snap);
      } catch {}
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [API]);

  const logSymptom = async () => {
    setLogging(true);
    try {
      await axios.post(`${API}/microbiome-twin/symptom`, {
        bristol, bloating, gas, note_bg: null,
      });
      toast.success("Симптомите са записани");
      setBristol(null); setBloating(0); setGas(0);
      await fetchAll();
    } catch {
      toast.error("Грешка при запис");
    }
    setLogging(false);
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="microbiome-twin-page">
      <MobileTopBar title="Микробиом" fallbackTo="/dashboard" testid="microbiome-twin-mobile-topbar" />
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  const score = snapshot?.diversity_score ?? 0;
  const scoreColor = score >= 70 ? "#10B981" : score >= 40 ? "#F59E0B" : "#EF4444";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="microbiome-twin-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Leaf className="w-5 h-5 text-emerald-500" />
          <h1 className="text-xl font-bold">🦠 Микробиом и храносмилане</h1>
        </div>

        {/* Score hero */}
        <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="score-card">
          <div className="text-[10px] uppercase opacity-50 mb-1">Разнообразие на микробиома · последни 7 дни</div>
          <div className="flex items-baseline gap-2">
            <div className="text-5xl font-bold" style={{ color: scoreColor }} data-testid="diversity-score">{score}</div>
            <div className="text-lg opacity-50">/ 100</div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${score}%`, background: scoreColor }} />
          </div>
          <p className="text-xs opacity-70 mt-3">{insight || "Запиши хранения и симптоми за персонализирани препоръки."}</p>
        </div>

        {/* iter1047 — абсорбция по етажи (gastro1) */}
        <div className="mb-4"><RefluxBrief isDark={isDark} /></div>
        <GiAbsorptionCard isDark={isDark} />

        {/* Components */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="plants-tile">
            <div className="text-[10px] uppercase opacity-50">Растения</div>
            <div className="text-2xl font-bold text-emerald-500">{snapshot?.plant_count || 0}<span className="text-sm opacity-60">/30</span></div>
            <div className="text-[10px] opacity-60 mt-0.5">Spector challenge</div>
          </div>
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="fermented-tile">
            <div className="text-[10px] uppercase opacity-50">Ферментирали</div>
            <div className="text-2xl font-bold text-amber-500">{snapshot?.fermented_count || 0}</div>
            <div className="text-[10px] opacity-60 mt-0.5">айран, кисело зеле…</div>
          </div>
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="prebiotic-tile">
            <div className="text-[10px] uppercase opacity-50">Пребиотици</div>
            <div className="text-2xl font-bold text-sky-500">{snapshot?.prebiotic_count || 0}</div>
            <div className="text-[10px] opacity-60 mt-0.5">лук, чесън, овес…</div>
          </div>
        </div>

        {/* Symptom logger */}
        <div className={`p-4 rounded-2xl border mb-4 ${cardCls}`} data-testid="symptom-logger">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-rose-500" />
            <h2 className="font-bold text-sm">Запиши симптоми (днес)</h2>
          </div>

          <div className="mb-3">
            <div className="text-[10px] uppercase opacity-50 mb-1.5">Bristol stool скала (1-7)</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6, 7].map((v) => (
                <button
                  key={v}
                  onClick={() => setBristol(v)}
                  data-testid={`bristol-${v}`}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                    bristol === v
                      ? "bg-rose-500 text-white"
                      : isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >{v}</button>
              ))}
            </div>
            <div className="text-[10px] opacity-50 mt-1">1=твърд запек · 4=идеален · 7=диария</div>
          </div>

          <div className="mb-3">
            <div className="text-[10px] uppercase opacity-50 mb-1.5">Подуване (0-10)</div>
            <input type="range" min="0" max="10" value={bloating} onChange={(e) => setBloating(parseInt(e.target.value))}
              className="w-full" data-testid="bloating-slider" />
            <div className="text-[10px] opacity-60">{bloating}/10</div>
          </div>

          <div className="mb-3">
            <div className="text-[10px] uppercase opacity-50 mb-1.5">Газове (0-10)</div>
            <input type="range" min="0" max="10" value={gas} onChange={(e) => setGas(parseInt(e.target.value))}
              className="w-full" data-testid="gas-slider" />
            <div className="text-[10px] opacity-60">{gas}/10</div>
          </div>

          <Button
            onClick={logSymptom}
            disabled={logging || bristol === null}
            data-testid="log-symptom-btn"
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
          >
            {logging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Запиши
          </Button>
        </div>

        {/* Recent symptoms summary */}
        {snapshot?.symptoms_summary?.samples > 0 && (
          <div className={`p-3 rounded-xl border ${cardCls}`} data-testid="symptoms-summary">
            <div className="text-[10px] uppercase opacity-50 mb-1">Последни 7 дни — средно</div>
            <div className="flex gap-4 text-sm">
              <div>Bristol: <b>{snapshot.symptoms_summary.avg_bristol ?? "—"}</b></div>
              <div>Подуване: <b>{snapshot.symptoms_summary.avg_bloating ?? "—"}/10</b></div>
              <div className="opacity-60">({snapshot.symptoms_summary.samples} записа)</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
